import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, X } from 'lucide-react';
import { clientsAPI, boardsAPI } from '../../../api';
import CreateBoardModal from '../../../components/CreateBoardModal';
import { overlayStyle, modalStyle, fmtDate, labelStyle } from '../utils';

const railAction = {
  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
  fontFamily: 'Playfair Display, serif', fontSize: 9.5, letterSpacing: '0.16em',
  textTransform: 'uppercase', color: '#cf9a3d',
};

export const ClientBoards = ({ clientId, client, t, lang, boards, allBoards, refresh, showToast }) => {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  const assignable = allBoards.filter((b) => !b.client_id);

  const handleCreate = async (boardData) => {
    try {
      const { data } = await boardsAPI.create({ ...boardData, client_name: boardData.client_name || client.name });
      await clientsAPI.boards.assign(clientId, data.id);
      setShowCreate(false);
      refresh();
      showToast(t.cs_board_created || 'Board created');
    } catch (e) {
      showToast(e.response?.data?.detail === 'board_limit_reached' ? "You've reached your current board limit." : (t.save_failed || 'Could not save'));
      setShowCreate(false);
    }
  };

  const handleAssign = async (boardId) => {
    try {
      await clientsAPI.boards.assign(clientId, boardId);
      setShowAssign(false);
      refresh();
      showToast(t.cs_board_assigned || 'Board assigned');
    } catch (e) { /* noop */ }
  };

  const handleUnassign = async (e, boardId) => {
    e.stopPropagation();
    try {
      await clientsAPI.boards.unassign(clientId, boardId);
      refresh();
    } catch (err) {
      showToast(t.save_failed || 'Could not save');
    }
  };

  return (
    <aside data-testid="client-boards-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--kahla-border)', paddingBottom: 12, marginBottom: 4 }}>
        <span style={{ ...labelStyle, margin: 0, fontSize: 12, letterSpacing: '0.2em', color: 'var(--kahla-charcoal)' }}>
          {t.cs_tab_boards || 'Boards'}
        </span>
        <div style={{ display: 'flex', gap: 16 }}>
          <button type="button" style={railAction} data-testid="assign-board-btn" onClick={() => setShowAssign(true)}>
            {t.cs_assign_short || 'Assign'}
          </button>
          <button type="button" style={railAction} data-testid="create-client-board-btn" onClick={() => setShowCreate(true)}>
            {t.cs_create_short || '+ New'}
          </button>
        </div>
      </div>

      {boards.length === 0 ? (
        <p data-testid="client-boards-empty" style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 13.5, color: 'var(--kahla-text-light)', lineHeight: 1.7, margin: '16px 0 0',
        }}>
          {t.cs_boards_empty_short || 'No boards yet — Client Space works independently, for history and documents alone.'}
        </p>
      ) : (
        <div>
          {boards.map((board) => (
            <div
              key={board.id}
              data-testid={`client-board-card-${board.id}`}
              onClick={() => navigate(`/editor/${board.id}`)}
              className="client-board-row"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0',
                borderBottom: '1px solid var(--kahla-border)', cursor: 'pointer',
              }}
            >
              <div style={{ width: 56, height: 40, flexShrink: 0, background: '#EAE3D6', overflow: 'hidden' }}>
                {board.cover_image_url && (
                  <img src={board.cover_image_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 13.5, color: 'var(--kahla-charcoal)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {board.name}
                </p>
                <p style={{ fontSize: 10.5, color: 'var(--kahla-text-light)', margin: '3px 0 0', letterSpacing: '0.04em' }}>
                  {board.image_count || 0} · {fmtDate(board.expires_at, lang)}
                </p>
              </div>
              <button
                type="button"
                data-testid={`unassign-board-${board.id}`}
                title={t.cs_unassign || 'Unassign'}
                onClick={(e) => handleUnassign(e, board.id)}
                style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: '#c9c1b2', lineHeight: 0 }}
              ><X size={12} strokeWidth={1.6} /></button>
              <ChevronRight size={13} strokeWidth={1.4} color="#c9c1b2" />
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateBoardModal onClose={() => setShowCreate(false)} onCreate={handleCreate} lang={lang} />
      )}

      {showAssign && (
        <div style={overlayStyle} onClick={() => setShowAssign(false)}>
          <div style={{ ...modalStyle, maxWidth: 480 }} onClick={(e) => e.stopPropagation()} data-testid="assign-board-modal">
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 400, margin: '0 0 20px', color: 'var(--kahla-charcoal)' }}>
              {t.cs_assign_pick || 'Assign a board'}
            </h2>
            {assignable.length === 0 ? (
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 15, color: 'var(--kahla-text-light)' }}>
                {t.cs_no_assignable || 'Every board is already assigned to a client.'}
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 0 }}>
                {assignable.map((b) => (
                  <button
                    key={b.id} type="button"
                    data-testid={`assign-option-${b.id}`}
                    onClick={() => handleAssign(b.id)}
                    style={{
                      background: 'transparent', border: 'none', borderBottom: '1px solid var(--kahla-border)',
                      padding: '13px 2px', cursor: 'pointer', textAlign: 'left',
                      fontFamily: 'Playfair Display, serif', fontSize: 15, color: 'var(--kahla-charcoal)',
                    }}
                  >
                    {b.name}
                    {b.client_name && <span style={{ fontSize: 12, color: 'var(--kahla-text-light)', marginLeft: 8 }}>{b.client_name}</span>}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" className="btn-ghost" onClick={() => setShowAssign(false)}>{t.cs_cancel || 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default ClientBoards;
